import { useSyncExternalStore } from 'react';

export type ThemePref = 'light' | 'dark' | 'system';

const KEY = 'mkcore.theme';
const listeners = new Set<() => void>();

function read(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

const media = () => window.matchMedia('(prefers-color-scheme: dark)');

export function resolvedTheme(pref: ThemePref = read()): 'light' | 'dark' {
  return pref === 'system' ? (media().matches ? 'dark' : 'light') : pref;
}

/** Applies the theme as a class on <html>; the tokens swap their values (CO-0023). */
export function applyTheme(pref: ThemePref = read()) {
  document.documentElement.classList.toggle('dark', resolvedTheme(pref) === 'dark');
}

export function setTheme(pref: ThemePref) {
  try {
    localStorage.setItem(KEY, pref);
  } catch {
    // ignore: the theme still applies for this session
  }
  applyTheme(pref);
  listeners.forEach((l) => l());
}

export function toggleTheme() {
  setTheme(resolvedTheme() === 'dark' ? 'light' : 'dark');
}

export function useTheme(): { pref: ThemePref; resolved: 'light' | 'dark' } {
  const pref = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      const m = media();
      m.addEventListener('change', cb);
      return () => {
        listeners.delete(cb);
        m.removeEventListener('change', cb);
      };
    },
    read,
    () => 'system' as ThemePref,
  );
  return { pref, resolved: resolvedTheme(pref) };
}
